// Payroll Calculation Utilities

/**
 * Calculate progressive income tax based on tax slabs
 * @param {number} annualIncome - Total annual taxable income
 * @param {Array} taxSlabs - Array of tax slab objects with minIncome, maxIncome, rate
 * @returns {number} Total tax amount
 */
export function calculateProgressiveTax(annualIncome, taxSlabs) {
  if (!annualIncome || !taxSlabs?.length) return 0;
  
  let tax = 0;
  const sortedSlabs = [...taxSlabs].sort((a, b) => a.minIncome - b.minIncome);
  
  for (const slab of sortedSlabs) {
    if (annualIncome <= slab.minIncome) break;
    
    const taxableInSlab = Math.min(
      annualIncome - slab.minIncome,
      slab.maxIncome - slab.minIncome
    );
    
    if (taxableInSlab > 0) {
      tax += taxableInSlab * (slab.rate / 100);
    }
  }
  
  return Math.round(tax);
}

/**
 * Calculate monthly TDS (Tax Deducted at Source)
 * @param {number} annualTax - Total annual tax
 * @returns {number} Monthly TDS amount
 */
export function calculateMonthlyTDS(annualTax) {
  return Math.round(annualTax / 12);
}

/**
 * Calculate Provident Fund contribution
 * @param {number} basicSalary - Basic salary component
 * @param {number} rate - PF rate percentage (default 12%)
 * @returns {object} Employee and employer contributions
 */
export function calculateProvidentFund(basicSalary, rate = 12) {
  const employeeContribution = Math.round(basicSalary * (rate / 100));
  const employerContribution = Math.round(basicSalary * (rate / 100));
  return {
    employee: employeeContribution,
    employer: employerContribution,
    total: employeeContribution + employerContribution
  };
}

/**
 * Calculate gross salary from components
 * @param {object} earnings - Object containing all earning components
 * @returns {number} Total gross salary
 */
export function calculateGrossSalary(earnings) {
  if (!earnings) return 0;
  return Object.values(earnings).reduce((sum, val) => sum + (Number(val) || 0), 0);
}

/**
 * Calculate total deductions
 * @param {object} deductions - Object containing all deduction components
 * @returns {number} Total deductions
 */
export function calculateTotalDeductions(deductions) {
  if (!deductions) return 0;
  return Object.values(deductions).reduce((sum, val) => sum + (Number(val) || 0), 0);
}

/**
 * Calculate safe total deductions with cap
 * Ensures deductions don't exceed a percentage of gross salary
 * @param {object} deductions - Object containing all deduction components
 * @param {number} grossSalary - Gross salary for capping
 * @param {number} maxPercentage - Maximum percentage of gross (default 40%)
 * @returns {object} Object with capped deductions and details
 */
export function calculateSafeDeductions(deductions, grossSalary, maxPercentage = 40) {
  if (!deductions || !grossSalary) return { deductions, total: 0, wasCapped: false };
  
  const total = Object.values(deductions).reduce((sum, val) => sum + (Number(val) || 0), 0);
  const maxAllowed = Math.round(grossSalary * (maxPercentage / 100));
  
  if (total <= maxAllowed) {
    return { deductions, total, wasCapped: false };
  }
  
  // Proportionally reduce all deductions to fit within cap
  const ratio = maxAllowed / total;
  const cappedDeductions = {};
  for (const [key, value] of Object.entries(deductions)) {
    cappedDeductions[key] = Math.round((Number(value) || 0) * ratio);
  }
  
  return {
    deductions: cappedDeductions,
    total: maxAllowed,
    wasCapped: true,
    originalTotal: total
  };
}

/**
 * Calculate net pay
 * @param {number} grossSalary - Total gross salary
 * @param {number} totalDeductions - Total deductions
 * @returns {number} Net pay amount
 */
export function calculateNetPay(grossSalary, totalDeductions) {
  return Math.max(0, grossSalary - totalDeductions);
}

/**
 * Calculate per day salary
 * @param {number} grossSalary - Monthly gross salary
 * @param {number} workingDays - Number of working days in month (default 22)
 * @returns {number} Per day salary
 */
export function calculatePerDaySalary(grossSalary, workingDays = 22) {
  return Math.round(grossSalary / workingDays);
}

/**
 * Calculate Loss of Pay (LOP) deduction
 * @param {number} perDaySalary - Per day salary
 * @param {number} lopDays - Number of LOP days
 * @returns {number} LOP deduction amount
 */
export function calculateLOPDeduction(perDaySalary, lopDays) {
  return Math.round(perDaySalary * lopDays);
}

/**
 * Calculate overtime pay
 * @param {number} hourlyRate - Hourly rate
 * @param {number} overtimeHours - Number of overtime hours
 * @param {number} multiplier - Overtime multiplier (default 1.5)
 * @returns {number} Overtime pay amount
 */
export function calculateOvertimePay(hourlyRate, overtimeHours, multiplier = 1.5) {
  return Math.round(hourlyRate * overtimeHours * multiplier);
}

/**
 * Calculate hourly rate from monthly salary
 * @param {number} monthlySalary - Monthly salary
 * @param {number} workingDays - Working days per month (default 22)
 * @param {number} hoursPerDay - Working hours per day (default 8)
 * @returns {number} Hourly rate
 */
export function calculateHourlyRate(monthlySalary, workingDays = 22, hoursPerDay = 8) {
  return Math.round(monthlySalary / (workingDays * hoursPerDay));
}

/**
 * Calculate professional tax based on salary
 * @param {number} grossSalary - Monthly gross salary
 * @returns {number} Professional tax amount
 */
export function calculateProfessionalTax(grossSalary) {
  // Standard professional tax slabs
  if (grossSalary <= 5000) return 0;
  if (grossSalary <= 10000) return 150;
  return 200;
}

/**
 * Generate complete payslip calculations
 * @param {object} salaryStructure - Employee's salary structure
 * @param {Array} taxSlabs - Tax slabs for calculation
 * @param {object} options - Additional options (lop, overtime, bonus, etc.)
 * @returns {object} Complete payslip data
 */
export function generatePayslipCalculations(salaryStructure, taxSlabs, options = {}) {
  const {
    lopDays = 0,
    overtimeHours = 0,
    bonus = 0,
    additionalDeductions = 0,
    workingDays = 22
  } = options;

  // Basic earnings
  const earnings = {
    basic: salaryStructure.basicSalary || 0,
    hra: salaryStructure.hra || 0,
    transportAllowance: salaryStructure.transportAllowance || 0,
    medicalAllowance: salaryStructure.medicalAllowance || 0,
    specialAllowance: salaryStructure.specialAllowance || 0,
    overtime: 0,
    bonus: bonus
  };

  // Calculate overtime if applicable
  if (overtimeHours > 0) {
    const hourlyRate = calculateHourlyRate(salaryStructure.grossSalary || calculateGrossSalary(earnings), workingDays);
    earnings.overtime = calculateOvertimePay(hourlyRate, overtimeHours);
  }

  const grossEarnings = calculateGrossSalary(earnings);

  // Calculate deductions using custom rates from salary structure
  const pfRate = salaryStructure.pfRate || 12;
  const pf = calculateProvidentFund(earnings.basic, pfRate);
  const professionalTax = salaryStructure.professionalTax !== undefined 
    ? salaryStructure.professionalTax 
    : calculateProfessionalTax(grossEarnings);
  const loanEmi = salaryStructure.loanEmi || 0;
  const otherDed = salaryStructure.otherDeductions || 0;
  
  // Annual calculations for tax
  const annualGross = grossEarnings * 12;
  const annualTax = calculateProgressiveTax(annualGross, taxSlabs);
  const monthlyTds = calculateMonthlyTDS(annualTax);

  // LOP deduction
  const perDaySalary = calculatePerDaySalary(grossEarnings, workingDays);
  const lopDeduction = calculateLOPDeduction(perDaySalary, lopDays);

  const deductions = {
    providentFund: pf.employee,
    healthInsurance: 200, // Standard health insurance
    professionalTax: professionalTax,
    incomeTax: monthlyTds,
    loanEmi: loanEmi,
    lopDeduction: lopDeduction,
    otherDeductions: additionalDeductions + otherDed
  };

  // Apply deduction cap to ensure deductions don't exceed 40% of gross
  const safeDeductionResult = calculateSafeDeductions(deductions, grossEarnings, 40);
  const finalDeductions = safeDeductionResult.deductions;
  const totalDeductions = safeDeductionResult.total;
  const netPay = calculateNetPay(grossEarnings, totalDeductions);

  return {
    earnings,
    deductions: finalDeductions,
    grossEarnings,
    totalDeductions,
    netPay,
    workingDays,
    daysWorked: workingDays - lopDays,
    lopDays,
    deductionsCapped: safeDeductionResult.wasCapped,
    taxDetails: {
      annualGross,
      annualTax,
      monthlyTds
    },
    employerContributions: {
      providentFund: pf.employer,
      healthInsurance: 200
    }
  };
}

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default USD)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'USD') {
  // Handle null, undefined, NaN, or invalid values
  const numAmount = Number(amount);
  if (amount === null || amount === undefined || isNaN(numAmount)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(0);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount);
}

/**
 * Calculate CTC (Cost to Company)
 * @param {number} grossSalary - Monthly gross salary
 * @param {object} employerContributions - Employer contributions (PF, insurance, etc.)
 * @returns {object} Monthly and annual CTC
 */
export function calculateCTC(grossSalary, employerContributions = {}) {
  const totalEmployerContrib = Object.values(employerContributions).reduce((sum, val) => sum + (Number(val) || 0), 0);
  const monthlyCTC = grossSalary + totalEmployerContrib;
  return {
    monthly: monthlyCTC,
    annual: monthlyCTC * 12
  };
}

/**
 * Calculate salary hike
 * @param {number} currentSalary - Current salary
 * @param {number} hikePercentage - Hike percentage
 * @returns {object} New salary and increment details
 */
export function calculateSalaryHike(currentSalary, hikePercentage) {
  const increment = Math.round(currentSalary * (hikePercentage / 100));
  return {
    currentSalary,
    hikePercentage,
    increment,
    newSalary: currentSalary + increment
  };
}

/**
 * Get payroll status badge color
 * @param {string} status - Payroll status
 * @returns {string} Tailwind CSS class for badge
 */
export function getPayrollStatusColor(status) {
  switch (status) {
    case 'paid': return 'bg-emerald-100 text-emerald-700';
    case 'generated': return 'bg-blue-100 text-blue-700';
    case 'processing': return 'bg-amber-100 text-amber-700';
    case 'draft': return 'bg-gray-100 text-gray-700';
    case 'approved': return 'bg-teal-100 text-teal-700';
    case 'pending': return 'bg-yellow-100 text-yellow-700';
    case 'rejected': return 'bg-red-100 text-red-700';
    case 'active': return 'bg-green-100 text-green-700';
    case 'completed': return 'bg-emerald-100 text-emerald-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

/**
 * Format payroll status for display
 * @param {string} status - Payroll status
 * @returns {string} Formatted status string
 */
export function formatPayrollStatus(status) {
  const statusMap = {
    'paid': 'Paid',
    'generated': 'Generated',
    'processing': 'Processing',
    'draft': 'Draft',
    'approved': 'Approved',
    'pending': 'Pending',
    'rejected': 'Rejected',
    'active': 'Active',
    'completed': 'Completed',
    'on_hold': 'On Hold'
  };
  return statusMap[status] || status;
}

/**
 * Calculate payroll summary for a period
 * @param {Array} payslips - Array of payslips for the period
 * @returns {object} Summary statistics
 */
export function calculatePayrollSummary(payslips) {
  if (!payslips?.length) {
    return {
      totalEmployees: 0,
      totalGross: 0,
      totalDeductions: 0,
      totalNet: 0,
      avgSalary: 0
    };
  }

  const totalGross = payslips.reduce((sum, p) => sum + (p.grossPay || p.grossEarnings || 0), 0);
  const totalDeductions = payslips.reduce((sum, p) => {
    // Handle both US payroll (totalPreTax + totalTaxes + totalPostTax) and Indian payroll (totalDeductions)
    const deductions = p.totalDeductions || ((p.totalPreTax || 0) + (p.totalTaxes || 0) + (p.totalPostTax || 0));
    return sum + (deductions || 0);
  }, 0);
  const totalNet = payslips.reduce((sum, p) => sum + (p.netPay || 0), 0);

  return {
    totalEmployees: payslips.length,
    totalGross,
    totalDeductions,
    totalNet,
    avgSalary: Math.round(totalGross / payslips.length)
  };
}

/**
 * Get leave type display info
 * @param {string} leaveType - Leave type code
 * @returns {object} Display info with label and color
 */
export function getLeaveTypeInfo(leaveType) {
  const types = {
    'annual': { label: 'Annual Leave', color: 'bg-blue-100 text-blue-700' },
    'sick': { label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
    'personal': { label: 'Personal Leave', color: 'bg-purple-100 text-purple-700' },
    'maternity': { label: 'Maternity Leave', color: 'bg-pink-100 text-pink-700' },
    'paternity': { label: 'Paternity Leave', color: 'bg-indigo-100 text-indigo-700' },
    'unpaid': { label: 'Unpaid Leave', color: 'bg-gray-100 text-gray-700' }
  };
  return types[leaveType] || { label: leaveType, color: 'bg-gray-100 text-gray-700' };
}

/**
 * Get reimbursement category info
 * @param {string} category - Reimbursement category
 * @returns {object} Display info with label and color
 */
export function getReimbursementCategoryInfo(category) {
  const categories = {
    'travel': { label: 'Travel', color: 'bg-blue-100 text-blue-700', icon: 'Plane' },
    'medical': { label: 'Medical', color: 'bg-red-100 text-red-700', icon: 'Heart' },
    'equipment': { label: 'Equipment', color: 'bg-purple-100 text-purple-700', icon: 'Monitor' },
    'training': { label: 'Training', color: 'bg-teal-100 text-teal-700', icon: 'GraduationCap' },
    'meals': { label: 'Meals', color: 'bg-orange-100 text-orange-700', icon: 'Utensils' },
    'other': { label: 'Other', color: 'bg-gray-100 text-gray-700', icon: 'FileText' }
  };
  return categories[category] || { label: category, color: 'bg-gray-100 text-gray-700', icon: 'FileText' };
}

/**
 * Get bonus type info
 * @param {string} type - Bonus type
 * @returns {object} Display info with label and color
 */
export function getBonusTypeInfo(type) {
  const types = {
    'performance': { label: 'Performance Bonus', color: 'bg-emerald-100 text-emerald-700' },
    'annual': { label: 'Annual Bonus', color: 'bg-blue-100 text-blue-700' },
    'referral': { label: 'Referral Bonus', color: 'bg-purple-100 text-purple-700' },
    'spot': { label: 'Spot Award', color: 'bg-amber-100 text-amber-700' },
    'signing': { label: 'Signing Bonus', color: 'bg-teal-100 text-teal-700' },
    'retention': { label: 'Retention Bonus', color: 'bg-indigo-100 text-indigo-700' }
  };
  return types[type] || { label: type, color: 'bg-gray-100 text-gray-700' };
}

/**
 * Calculate working days in a month (excluding weekends)
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {number} Number of working days
 */
export function getWorkingDaysInMonth(month, year) {
  let workingDays = 0;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    // Exclude Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays++;
    }
  }
  
  return workingDays;
}

/**
 * Calculate attendance-based salary adjustments
 * @param {Array} attendanceRecords - Array of attendance records for the period
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @param {Array} leaveRecords - Optional approved leave records
 * @returns {object} Attendance summary with LOP days, overtime hours, present days
 */
export function calculateAttendanceAdjustments(attendanceRecords, month, year, leaveRecords = []) {
  const workingDays = getWorkingDaysInMonth(month, year);
  
  // Count present days from attendance
  const presentDays = attendanceRecords.filter(r => r.status === 'present').length;
  
  // Count approved leave days for the period
  const periodStart = new Date(year, month, 1).getTime();
  const periodEnd = new Date(year, month + 1, 0, 23, 59, 59).getTime();
  
  let approvedLeaveDays = 0;
  for (const leave of leaveRecords) {
    if (leave.status !== 'approved') continue;
    
    const leaveStart = Math.max(leave.startDate, periodStart);
    const leaveEnd = Math.min(leave.endDate, periodEnd);
    
    if (leaveEnd >= leaveStart) {
      // Count only working days in the leave period
      const start = new Date(leaveStart);
      const end = new Date(leaveEnd);
      let leaveDayCount = 0;
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          leaveDayCount++;
        }
      }
      approvedLeaveDays += leaveDayCount;
    }
  }
  
  // Calculate LOP: working days - present days - approved leaves
  const lopDays = Math.max(0, workingDays - presentDays - approvedLeaveDays);
  
  // Calculate total overtime hours
  const totalOvertimeHours = attendanceRecords.reduce(
    (sum, r) => sum + (Number(r.overtime) || 0), 
    0
  );
  
  // Calculate late arrivals
  const lateDays = attendanceRecords.filter(r => {
    if (!r.checkIn || r.status !== 'present') return false;
    const checkInTime = new Date(r.checkIn);
    return checkInTime.getHours() > 9 || (checkInTime.getHours() === 9 && checkInTime.getMinutes() > 15);
  }).length;
  
  // Calculate average work hours
  const totalWorkHours = attendanceRecords.reduce(
    (sum, r) => sum + (Number(r.workHours) || 0), 
    0
  );
  const avgWorkHours = presentDays > 0 ? totalWorkHours / presentDays : 0;
  
  return {
    workingDays,
    presentDays,
    approvedLeaveDays,
    lopDays,
    overtimeHours: Math.round(totalOvertimeHours * 100) / 100,
    lateDays,
    avgWorkHours: Math.round(avgWorkHours * 100) / 100,
    attendancePercentage: workingDays > 0 ? Math.round((presentDays / workingDays) * 100) : 0
  };
}

/**
 * Generate payslip with attendance-based adjustments
 * @param {object} salaryStructure - Employee's salary structure
 * @param {Array} taxSlabs - Tax slabs for calculation
 * @param {object} attendanceSummary - Result from calculateAttendanceAdjustments
 * @param {object} options - Additional options (bonus, etc.)
 * @returns {object} Complete payslip data with attendance adjustments
 */
export function generatePayslipWithAttendance(salaryStructure, taxSlabs, attendanceSummary, options = {}) {
  const { bonus = 0, additionalDeductions = 0 } = options;
  
  return generatePayslipCalculations(salaryStructure, taxSlabs, {
    lopDays: attendanceSummary.lopDays,
    overtimeHours: attendanceSummary.overtimeHours,
    bonus,
    additionalDeductions,
    workingDays: attendanceSummary.workingDays
  });
}

/**
 * Calculate pro-rated salary when salary change occurs mid-month
 * @param {object} oldSalaryStructure - Old salary structure
 * @param {object} newSalaryStructure - New salary structure  
 * @param {number} changeDate - Timestamp when salary change became effective
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {object} Pro-rated salary breakdown
 */
export function calculateProRatedSalary(oldSalaryStructure, newSalaryStructure, changeDate, month, year) {
  const workingDays = getWorkingDaysInMonth(month, year);
  const startOfMonth = new Date(year, month, 1);
  const changeDay = new Date(changeDate);
  
  // Count working days before and after the change
  let daysBeforeChange = 0;
  let daysAfterChange = 0;
  const endOfMonth = new Date(year, month + 1, 0);
  
  for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
    const dayOfWeek = d.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not weekend
      if (d < changeDay) {
        daysBeforeChange++;
      } else {
        daysAfterChange++;
      }
    }
  }
  
  // Calculate per-day salary for old and new structures
  const oldGrossSalary = oldSalaryStructure?.grossSalary || 
    (oldSalaryStructure?.basicSalary || 0) + 
    (oldSalaryStructure?.hra || 0) + 
    (oldSalaryStructure?.transportAllowance || 0) + 
    (oldSalaryStructure?.medicalAllowance || 0) + 
    (oldSalaryStructure?.specialAllowance || 0);
    
  const newGrossSalary = newSalaryStructure?.grossSalary || 
    (newSalaryStructure?.basicSalary || 0) + 
    (newSalaryStructure?.hra || 0) + 
    (newSalaryStructure?.transportAllowance || 0) + 
    (newSalaryStructure?.medicalAllowance || 0) + 
    (newSalaryStructure?.specialAllowance || 0);
  
  const oldPerDaySalary = calculatePerDaySalary(oldGrossSalary, workingDays);
  const newPerDaySalary = calculatePerDaySalary(newGrossSalary, workingDays);
  
  // Calculate pro-rated amounts
  const oldPeriodAmount = oldPerDaySalary * daysBeforeChange;
  const newPeriodAmount = newPerDaySalary * daysAfterChange;
  const totalProRatedGross = oldPeriodAmount + newPeriodAmount;
  
  // Calculate pro-rated earnings breakdown
  const calculateProRatedComponent = (oldVal, newVal) => {
    const oldComponent = calculatePerDaySalary(oldVal || 0, workingDays) * daysBeforeChange;
    const newComponent = calculatePerDaySalary(newVal || 0, workingDays) * daysAfterChange;
    return Math.round(oldComponent + newComponent);
  };
  
  const earnings = {
    basic: calculateProRatedComponent(oldSalaryStructure?.basicSalary, newSalaryStructure?.basicSalary),
    hra: calculateProRatedComponent(oldSalaryStructure?.hra, newSalaryStructure?.hra),
    transportAllowance: calculateProRatedComponent(oldSalaryStructure?.transportAllowance, newSalaryStructure?.transportAllowance),
    medicalAllowance: calculateProRatedComponent(oldSalaryStructure?.medicalAllowance, newSalaryStructure?.medicalAllowance),
    specialAllowance: calculateProRatedComponent(oldSalaryStructure?.specialAllowance, newSalaryStructure?.specialAllowance),
    overtime: 0,
    bonus: 0
  };
  
  return {
    isProRated: true,
    workingDays,
    daysBeforeChange,
    daysAfterChange,
    changeDate: changeDate,
    oldSalary: {
      perDay: oldPerDaySalary,
      total: oldPeriodAmount,
      grossMonthly: oldGrossSalary
    },
    newSalary: {
      perDay: newPerDaySalary,
      total: newPeriodAmount,
      grossMonthly: newGrossSalary
    },
    earnings,
    grossEarnings: Math.round(totalProRatedGross),
    salaryIncrease: newGrossSalary - oldGrossSalary,
    salaryIncreasePercentage: oldGrossSalary > 0 ? Math.round(((newGrossSalary - oldGrossSalary) / oldGrossSalary) * 100) : 0
  };
}

/**
 * Generate payslip with pro-rated salary for mid-month changes
 * @param {object} oldSalaryStructure - Old salary structure (null if no change)
 * @param {object} newSalaryStructure - Current/New salary structure
 * @param {number|null} changeDate - Salary change effective date (null if no change in period)
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @param {Array} taxSlabs - Tax slabs
 * @param {object} options - Additional options
 * @returns {object} Complete payslip with pro-rated calculations if applicable
 */
export function generateProRatedPayslip(oldSalaryStructure, newSalaryStructure, changeDate, month, year, taxSlabs, options = {}) {
  const {
    lopDays = 0,
    overtimeHours = 0,
    bonus = 0,
    additionalDeductions = 0
  } = options;
  
  const workingDays = getWorkingDaysInMonth(month, year);
  const startOfMonth = new Date(year, month, 1).getTime();
  const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59).getTime();
  
  // Check if salary change is in this period
  const hasChange = changeDate && oldSalaryStructure && 
    changeDate >= startOfMonth && changeDate <= endOfMonth;
  
  if (!hasChange) {
    // No mid-month change, use standard calculation
    return generatePayslipCalculations(newSalaryStructure, taxSlabs, {
      lopDays,
      overtimeHours,
      bonus,
      additionalDeductions,
      workingDays
    });
  }
  
  // Calculate pro-rated salary
  const proRated = calculateProRatedSalary(oldSalaryStructure, newSalaryStructure, changeDate, month, year);
  
  // Add overtime
  if (overtimeHours > 0) {
    const avgHourlyRate = calculateHourlyRate(proRated.grossEarnings, workingDays);
    proRated.earnings.overtime = calculateOvertimePay(avgHourlyRate, overtimeHours);
  }
  
  // Add bonus
  proRated.earnings.bonus = bonus;
  
  // Recalculate gross with overtime and bonus
  const grossEarnings = calculateGrossSalary(proRated.earnings);
  
  // Calculate deductions based on pro-rated amounts
  const pf = calculateProvidentFund(proRated.earnings.basic);
  const professionalTax = calculateProfessionalTax(grossEarnings);
  
  // Annual calculations for tax (use new salary for annual projection)
  const projectedAnnualGross = newSalaryStructure.grossSalary * 12;
  const annualTax = calculateProgressiveTax(projectedAnnualGross, taxSlabs);
  const monthlyTds = calculateMonthlyTDS(annualTax);
  
  // LOP deduction (based on average per day)
  const avgPerDaySalary = calculatePerDaySalary(grossEarnings, workingDays);
  const lopDeduction = calculateLOPDeduction(avgPerDaySalary, lopDays);
  
  const deductions = {
    providentFund: pf.employee,
    healthInsurance: 200,
    professionalTax: professionalTax,
    incomeTax: monthlyTds,
    loanEmi: 0,
    lopDeduction: lopDeduction,
    otherDeductions: additionalDeductions
  };
  
  const totalDeductions = calculateTotalDeductions(deductions);
  const netPay = calculateNetPay(grossEarnings, totalDeductions);
  
  return {
    earnings: proRated.earnings,
    deductions,
    grossEarnings,
    totalDeductions,
    netPay,
    workingDays,
    daysWorked: workingDays - lopDays,
    lopDays,
    isProRated: true,
    proRatedDetails: {
      daysBeforeChange: proRated.daysBeforeChange,
      daysAfterChange: proRated.daysAfterChange,
      oldSalaryDays: proRated.daysBeforeChange,
      newSalaryDays: proRated.daysAfterChange,
      oldGrossMonthly: proRated.oldSalary.grossMonthly,
      newGrossMonthly: proRated.newSalary.grossMonthly,
      oldPeriodAmount: proRated.oldSalary.total,
      newPeriodAmount: proRated.newSalary.total,
      changeDate: proRated.changeDate,
      salaryIncrease: proRated.salaryIncrease,
      salaryIncreasePercentage: proRated.salaryIncreasePercentage
    },
    taxDetails: {
      annualGross: projectedAnnualGross,
      annualTax,
      monthlyTds
    },
    employerContributions: {
      providentFund: pf.employer,
      healthInsurance: 200
    }
  };
}


